/**
 * Firebase Storage seam.
 *
 * Two SDKs implement this contract: the JS `firebase/storage` container below (web) and a native
 * `@react-native-firebase/storage` container on React Native (ELARA-982). So the surface is
 * expressed in platform-neutral terms — an opaque {@link StorageReference} handle and an
 * {@link Asset}-oriented upload — rather than re-exporting `firebase/storage` functions whose
 * argument types are JS-SDK specific.
 *
 * The upload is asset-oriented (not byte-oriented) because the two SDKs want different payloads:
 * the JS SDK uploads a `Blob`, while RNFB's native path uploads a local file `uri` via `putFile`.
 * Handing RNFB bytes instead would route through `Base64.fromData()`, copying the whole file into a
 * JS base64 string before it ever reaches native — on top of the RN `Blob`-polyfill hazards that
 * {@link assetToUploadData} documents. `Asset` already carries exactly one byte source per platform,
 * so each container picks its own native path from it.
 */
import type { StorageReference as FbStorageReference } from 'firebase/storage';

import type { FirebaseApp } from 'firebase/app';

import type { Asset } from '#lib/asset.js';

import { connectStorageEmulator, getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';

import { createContainer } from '@elara/core/di';

import { assetToUploadData } from '#lib/asset.js';

export interface FirebaseStorageConfig {
  bucketUrl?: string;
}

declare const storageReferenceBrand: unique symbol;

/**
 * An opaque handle to a location in the storage bucket. The concrete value is the active SDK's
 * reference object; consumers only ever pass it back to {@link StorageContainer.getDownloadURL},
 * so branding it keeps the two SDKs' reference types from leaking into shared code.
 */
export type StorageReference = { readonly [storageReferenceBrand]: 'StorageReference' };

/** Reports upload progress as a percentage (0-100). */
export type UploadProgressCallback = (percent: number) => void;

/**
 * Creates the JS-SDK storage container (web).
 *
 * @param app - The Firebase app the bucket belongs to.
 * @param config - Optional bucket override.
 */
export function createStorageContainer(app: FirebaseApp, config: FirebaseStorageConfig | undefined) {
  const $storage = createContainer('storage')
    // SDK-SPECIFIC — see the note on `FirebaseAuthContainer`'s `backend`.
    .providedLazy('backend', () => getStorage(app, config?.bucketUrl))
    .dependent('ref', ['backend'], ({ backend }) => {
      return (path: string): StorageReference => ref(backend, path) as unknown as StorageReference;
    })
    .provided('getDownloadURL', async (reference: StorageReference): Promise<string> => {
      return getDownloadURL(reference as unknown as FbStorageReference);
    })
    .dependent('uploadAsset', ['backend'], ({ backend }) => {
      /**
       * Uploads an asset resumably and resolves with its download URL. Progress is reported as a
       * percentage so callers stay free of SDK snapshot types.
       */
      return async (path: string, asset: Asset, onProgress?: UploadProgressCallback): Promise<string> => {
        const uploadData = await assetToUploadData(asset);
        const uploadTask = uploadBytesResumable(
          ref(backend, path),
          uploadData,
          asset.mimeType ? { contentType: asset.mimeType } : undefined,
        );
        return new Promise<string>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => onProgress?.((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
            reject,
            () => {
              getDownloadURL(uploadTask.snapshot.ref).then(resolve, reject);
            },
          );
        });
      };
    })
    .dependent('connectEmulator', ['backend'], ({ backend }) => {
      return (host: string, port: number): void => connectStorageEmulator(backend, host, port);
    });
  return $storage;
}

export type $StorageContainer = ReturnType<typeof createStorageContainer>;
export type StorageContainer = $StorageContainer['use'];
